import type { AlertView, HealthSample, NodeStatus } from '@occ/contracts';
import { StatusDot } from '../components/ui';
import { timeAgo } from '../lib/format';
import {
  formatMetricValue,
  METRIC_LABEL,
  SEVERITY_BADGE_CLASSES,
  SEVERITY_LABEL,
} from './alertInfo';
import { HoverShell } from './HoverShell';

/**
 * Hover completo del nodo hoja (sección 7.1 del spec).
 *
 * `agentVersion` queda deliberadamente fuera: el spec solo pide mostrarla
 * "cuando una acción está deshabilitada por versión", y las acciones llegan
 * recién en la fase 4 — no hay de dónde traer ese motivo todavía.
 */
export type LeafHoverData = {
  alias: string;
  status: NodeStatus;
  lastSeenAt: Date | null;
  breadcrumb: string | null;
  alert: AlertView | null;
  health: HealthSample | null;
};

export function LeafHoverCard({ data, x, y }: { data: LeafHoverData; x: number; y: number }) {
  return (
    <HoverShell x={x} y={y}>
      <div className="font-semibold tracking-tight">{data.alias}</div>
      {data.breadcrumb && <div className="text-[11px] opacity-60">{data.breadcrumb}</div>}
      <StatusDot status={data.status} withLabel />
      <div className="opacity-70">Visto {timeAgo(data.lastSeenAt)}</div>

      {data.alert && (
        <div className="grid gap-0.5 border-t border-black/10 pt-1 dark:border-white/10">
          <div className="flex items-center gap-1.5">
            <span
              className={`rounded border px-1.5 py-0.5 text-[11px] leading-4 ${SEVERITY_BADGE_CLASSES[data.alert.severity]}`}
            >
              {SEVERITY_LABEL[data.alert.severity]}
            </span>
            <span>{METRIC_LABEL[data.alert.metric]}</span>
          </div>
          {formatMetricValue(data.alert) && (
            <div className="opacity-70">{formatMetricValue(data.alert)}</div>
          )}
          <div className="opacity-70">Desde hace {timeAgo(data.alert.firstBreachAt)}</div>
          {data.alert.acknowledgedAt && (
            // `acknowledgedByUserId` va tal cual: resolverlo a un nombre exige el
            // listado de usuarios, que esta vista no trae y no es su alcance.
            <div className="opacity-70">
              Reconocida por {data.alert.acknowledgedByUserId}
              {data.alert.acknowledgeNote ? ` · ${data.alert.acknowledgeNote}` : ''}
            </div>
          )}
        </div>
      )}

      {data.health && (
        <div className="grid gap-1 border-t border-black/10 pt-1 dark:border-white/10">
          <MiniBar label="CPU" value={data.health.cpuPercent} />
          <MiniBar label="Memoria" value={data.health.memoryUsedPercent} />
          <MiniBar label="Disco" value={data.health.diskUsedPercent} />
        </div>
      )}
    </HoverShell>
  );
}

function MiniBar({ label, value }: { label: string; value: number | undefined }) {
  if (value === undefined) return null;
  const width = Math.min(100, Math.max(0, value));
  return (
    <div className="grid gap-0.5">
      <div className="flex justify-between text-[11px] opacity-70">
        <span>{label}</span>
        <span>{Math.round(value)}%</span>
      </div>
      <div className="h-1 rounded bg-black/10 dark:bg-white/10">
        <div className="h-full rounded bg-[var(--occ-brand)]" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}
